/**
 * Display formatters shared across screens.
 *
 * Each of these existed two or three times over under slightly different names
 * (`fmtDate`, `formatDate`, `getUserName`, `userLabel`, …) and with slightly
 * different behaviour — some rendered a missing value as `-`, others as `—`,
 * one as an empty cell.
 */

/** Placeholder for a value the record does not carry. */
export const EMPTY = '-';

export const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return EMPTY;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? EMPTY : date.toLocaleDateString('tr-TR');
};

export const formatDateTime = (value: string | Date | null | undefined): string => {
  if (!value) return EMPTY;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? EMPTY : date.toLocaleString('tr-TR');
};

export const formatNumber = (value: number | null | undefined): string =>
  value == null ? EMPTY : new Intl.NumberFormat('tr-TR').format(value);

interface NamedPerson {
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  email?: string | null;
}

/** Full name, falling back through username and email before giving up. */
export const personDisplayName = (person: NamedPerson | null | undefined): string => {
  if (!person) return EMPTY;
  const full = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return full || person.username || person.email || EMPTY;
};

/** Same idea for the contract service, which names the fields differently. */
export const contactDisplayName = (
  person: { name?: string | null; surname?: string | null } | null | undefined
): string => {
  if (!person) return EMPTY;
  return [person.name, person.surname].filter(Boolean).join(' ').trim() || EMPTY;
};

/** Up to two uppercase initials for an avatar fallback. */
export const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

/**
 * Non-negative integer from a number input. `min={0}` only constrains the
 * spinner arrows — typing or pasting "-5" still reaches onChange, and every
 * form was passing it straight into state.
 */
export const parseNonNegativeInt = (value: string): number => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};
